import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { LabelEnum } from 'app/shared/enums';
import { isCalculationForm } from 'app/shared/helpers/isCalculationForm';
import { NewButton } from 'app/components/NewButton';
import { SkipLinkProps } from './SkipLink.interfaces';
import { useStyles } from './SkipLink.styles';

const SkipLink = (props: SkipLinkProps) => {
  const classes = useStyles();
  const { pathname } = useLocation();
  const isTravelForm = useMemo(
    () => pathname.includes('kalkulator-ubezpieczenia-turystycznego'),
    [pathname]
  );
  const isCheckout = useMemo(
    () => pathname.includes('checkout') || pathname.includes('payment'),
    [pathname]
  );
  const isResults = useMemo(
    () => pathname.includes('/results') || pathname.includes('/travel-results'),
    [pathname]
  );

  const mainContentSelectorConfig = {
    calculationForm: {
      selector: 'div[data-field-type="question"]',
      marginTop: 120,
    },
    travelForm: {
      selector: 'div[data-field-type="question"]',
      marginTop: 144,
    },
    checkout: {
      // upsale, checkout or payment
      selector: 'span[data-cy="back-button"]',
      marginTop: 100,
    },
    results: {
      selector: 'span[data-cy="back-to-calculation-btn"]',
      marginTop: 0,
    },
  };

  const configKey = useMemo(
    () =>
      isCalculationForm() && isTravelForm
        ? 'travelForm'
        : isCalculationForm()
        ? 'calculationForm'
        : isCheckout
        ? 'checkout'
        : isResults
        ? 'results'
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname]
  );

  const isVisible = (el: HTMLElement): boolean => {
    let current: HTMLElement | null = el;

    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      if (
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.opacity === '0'
      ) {
        return false;
      }
      current = current.parentElement;
    }

    return true;
  };

  const onClick = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();

    if (!configKey) return;

    const config = mainContentSelectorConfig[configKey];
    const elements = Array.from(
      document.querySelectorAll(config.selector)
    ) as HTMLElement[];
    const target = elements.find(isVisible);

    if (target) {
      target.style.setProperty('scroll-margin-top', `${config.marginTop}px`);
      target.scrollIntoView({ behavior: 'smooth' });
      target.focus();

      if (!CSS.supports('scroll-margin-top', '1px')) {
        setTimeout(() => {
          window.scrollBy(0, -config.marginTop);
        });
      }
    }

    (event.currentTarget as HTMLElement).blur();
  };

  if (!configKey) return <></>;

  return (
    <NewButton
      disableRipple
      className={classes.root}
      onClick={onClick}
      variant={'contained'}
      title={LabelEnum.GoToMainContent}
    />
  );
};

export default SkipLink;
